import { CustomTooltip, IconButton } from '@fountain/fountain-ui-components';
import {
  Icon,
  PencilOutlineIcon,
  TrashOutlineIcon,
} from '@fountain/ripple/icon';
import { Grid, Typography } from '@material-ui/core';
import {
  RestrictedLocation,
  WorkflowSchedulerStage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import { useSimpleToggle } from 'hooks';
import produce from 'immer';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { ConfirmationModal } from '../../../../../../../../components/ConfirmationModal';
import { AddressFormModal } from '../AddressFormModal';
import messages from './messages';
import { useStyles } from './styles';

export interface AddressRowProps {
  restrictedLocation: RestrictedLocation;
  setStage: React.Dispatch<React.SetStateAction<WorkflowStageDetail>>;
  stage: WorkflowSchedulerStage;
}

export const AddressRow: FC<AddressRowProps> = ({
  restrictedLocation,
  setStage,
  stage,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const {
    showContent: isDeleteMessageModalOpen,
    toggle: toggleIsDeleteMessageModalOpen,
  } = useSimpleToggle(false);

  const {
    showContent: isAddressFormModalOpen,
    off: closeAddressFormModal,
    on: openAddressFormModal,
  } = useSimpleToggle(false);

  const onConfirmDelete = () => {
    setStage(
      produce(stage, draftStage => {
        const draftRestrictedLocations =
          (draftStage.additional_info &&
            draftStage.additional_info.restricted_locations_attributes) ||
          [];

        if (draftRestrictedLocations.length > 0) {
          const idx = draftRestrictedLocations.findIndex(
            location => location.location === restrictedLocation?.location,
          );

          draftRestrictedLocations[idx] = {
            ...restrictedLocation,
            _destroy: true,
          };
        }
      }),
    );
  };

  return (
    <>
      <Grid
        container
        className={styles.row}
        alignItems="center"
        justify="space-between"
      >
        <Typography variant="body2" className={styles.locationText}>
          {restrictedLocation.location}
        </Typography>
        <Grid>
          <CustomTooltip dense title={intl.formatMessage(globalMessages.edit)}>
            <IconButton
              disableRipple
              aria-label={intl.formatMessage(globalMessages.edit)}
              className={styles.editMessageButton}
              onClick={openAddressFormModal}
            >
              <Icon
                icon={PencilOutlineIcon}
                size="sm"
                color="inherit"
                className={styles.editMessageIcon}
              />
            </IconButton>
          </CustomTooltip>
          <CustomTooltip
            dense
            title={intl.formatMessage(globalMessages.delete)}
          >
            <IconButton
              disableRipple
              aria-label={intl.formatMessage(globalMessages.delete)}
              className={styles.deleteMessageButton}
              onClick={toggleIsDeleteMessageModalOpen}
            >
              <Icon
                icon={TrashOutlineIcon}
                size="xs"
                color="inherit"
                className={styles.deleteMessageIcon}
              />
            </IconButton>
          </CustomTooltip>
        </Grid>
      </Grid>
      {isAddressFormModalOpen && (
        <AddressFormModal
          isOpen={isAddressFormModalOpen}
          onClose={closeAddressFormModal}
          restrictedLocation={restrictedLocation}
          setStage={setStage}
          stage={stage}
        />
      )}
      {isDeleteMessageModalOpen && (
        <ConfirmationModal
          bodyContent={<FormattedMessage {...messages.askDelete} />}
          cancelButtonContent={<FormattedMessage {...globalMessages.cancel} />}
          confirmButtonContent={<FormattedMessage {...globalMessages.delete} />}
          negative
          onCancel={toggleIsDeleteMessageModalOpen}
          onConfirm={onConfirmDelete}
          title={intl.formatMessage(messages.deleteOnsiteAddress)}
        />
      )}
    </>
  );
};
